import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { FileText, Link as LinkIcon, Search, ExternalLink, Download, FolderOpen, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { PortalLayout } from "@/components/portal-layout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEntitlements } from "@/hooks/use-entitlements";
import { UpgradeOverlay } from "@/components/upgrade-overlay";
import { listDocuments } from "@/lib/documents.functions";

export const Route = createFileRoute("/teacher/documents")({ component: TeacherDocs });

function TeacherDocs() {
  return <DocsViewer portal="teacher" />;
}

export function DocsViewer({ portal }: { portal: "teacher" | "student" }) {
  const ent = useEntitlements();
  const listFn = useServerFn(listDocuments);
  const [search, setSearch] = React.useState("");
  const [filterFolder, setFilterFolder] = React.useState<string>("all");

  const q = useQuery({
    queryKey: ["documents", portal],
    enabled: ent.documentsEnabled,
    queryFn: () => listFn({}),
  });

  const folders = q.data?.folders ?? [];
  const docs = q.data?.documents ?? [];

  const filtered = React.useMemo(() => {
    return docs.filter((d: any) => {
      if (filterFolder !== "all" && d.folder_id !== filterFolder) return false;
      if (search && !d.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [docs, search, filterFolder]);

  if (!ent.isLoading && !ent.documentsEnabled) {
    return (
      <PortalLayout portal={portal} breadcrumb={[{ label: portal === "teacher" ? "Giáo viên" : "Học sinh" }, { label: "Tài liệu" }]}>
        <UpgradeOverlay
          feature="Thư viện Tài liệu"
          description="Truy cập kho tài liệu của trung tâm."
        />
      </PortalLayout>
    );
  }

  return (
    <PortalLayout portal={portal} breadcrumb={[{ label: portal === "teacher" ? "Giáo viên" : "Học sinh" }, { label: "Tài liệu" }]}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-5">
          <h1 className="font-display text-2xl font-semibold">Tài liệu học tập</h1>
          <p className="text-sm text-muted-foreground">Tài liệu do trung tâm chia sẻ</p>
        </div>

        <div className="bg-card border rounded-2xl p-4 mb-5 flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Tìm tài liệu..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={filterFolder} onValueChange={setFilterFolder}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Danh mục" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả danh mục</SelectItem>
              {folders.map((f: any) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {q.isLoading ? (
          <div className="text-center py-8 text-muted-foreground"><Loader2 className="size-5 inline animate-spin" /> Đang tải...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-2xl">
            <FolderOpen className="size-10 mx-auto mb-2 opacity-30" />
            Chưa có tài liệu nào
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((d: any) => {
              const isFile = d.doc_type === "file";
              const Icon = isFile ? FileText : LinkIcon;
              const url = isFile ? d.signed_url : d.external_url;
              const folderName = folders.find((f: any) => f.id === d.folder_id)?.name;
              return (
                <div key={d.id} className="bg-card border rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className="size-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 grid place-items-center shrink-0">
                      <Icon className="size-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{d.title}</div>
                      {d.description && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{d.description}</div>}
                      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground">
                        {folderName && <Badge variant="outline" className="text-[10px]">{folderName}</Badge>}
                        <span>{format(new Date(d.created_at), "dd/MM/yy", { locale: vi })}</span>
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="gap-1.5" disabled={!url} onClick={() => url && window.open(url, "_blank")}>
                    {isFile ? <Download className="size-3.5" /> : <ExternalLink className="size-3.5" />}
                    {isFile ? "Xem / Tải" : "Mở liên kết"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </PortalLayout>
  );
}
